import type { RxJsonSchema } from 'rxdb'

/**
 * PersonalNoteContent Document Type
 * Stores personal note content separately from metadata to enable
 * independent sync and version-based conflict resolution.
 */
export interface PersonalNoteContentDocument {
  id: string
  personalNoteId: string
  envId: string
  isDeleted: boolean
  content: string
  format: string // 'tiptap', 'markdown', 'plain'
  length: number
  plainText?: string | null
  version: number

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for PersonalNoteContent
 */
export const personalNoteContentSchema: RxJsonSchema<PersonalNoteContentDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    personalNoteId: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },
    content: {
      type: 'string',
      maxLength: 100000,
      default: '',
    },
    format: {
      type: 'string',
      enum: ['tiptap', 'markdown', 'plain'],
      default: 'tiptap',
    },
    length: {
      type: 'number',
      minimum: 0,
      maximum: 1000000,
      multipleOf: 1,
      default: 0,
    },
    plainText: {
      type: ['string', 'null'],
      maxLength: 100000,
    },
    version: {
      type: 'number',
      minimum: 1,
      maximum: 999999999,
      multipleOf: 1,
      default: 1,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'personalNoteId',
    'envId',
    'isDeleted',
    'content',
    'format',
    'length',
    'version',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'personalNoteId',
    'envId',
    'updatedOn',
    ['envId', 'isDeleted'],
    ['personalNoteId', 'isDeleted'],
    ['envId', 'updatedOn'],
  ],
}
